export type EllmoApiType = 'semantic' | 'structured';

/**
 * Fetch data from Ellmo API
 * Use this in getServerSideProps, getStaticProps, or getInitialProps
 * 
 * @example
 * ```tsx
 * // In getServerSideProps (Next.js Pages Router)
 * export async function getServerSideProps(context) {
 *   const { data, error } = await fetchEllmoData({
 *     clientId: process.env.ELLMO_CLIENT_ID!,
 *     url: context.resolvedUrl,
 *     type: 'semantic'
 *   });
 *   
 *   return {
 *     props: { ellmoData: data, ellmoError: error }
 *   };
 * }
 * 
 * // In getInitialProps (older Next.js API)
 * MyPage.getInitialProps = async (ctx) => {
 *   const { data, error } = await fetchEllmoData({
 *     clientId: process.env.NEXT_PUBLIC_ELLMO_CLIENT_ID!,
 *     url: ctx.asPath || '/',
 *     type: 'semantic'
 *   });
 *   
 *   return { ellmoData: data, ellmoError: error };
 * };
 * ```
 */
export async function fetchEllmoData({
  clientId,
  url,
  type = 'semantic'
}: {
  clientId: string;
  url: string;
  type?: EllmoApiType;
}): Promise<{ data: string | null; error: string | null }> {
  try {
    // Validate required props
    if (!clientId) {
      throw new Error('clientId is required. Please provide your Ellmo API client identifier.');
    }

    if (!url) {
      throw new Error('url is required. Please provide the page pathname.');
    }

    if (url.includes('.')) {
      // Sometimes urls contain a dot if they're images or other assets which are served by SSG (static generation)
      return { data: null, error: null };
    }

    // Encode the URL path for the API call
    const encodedUrl = encodeURIComponent(url);
    const translatedType = type === 'structured' ? 'structured' : 'semantic';

    // Build the API URL
    const apiUrl = `https://www.tryellmo.ai/public-api/wordpress?c=${clientId}&t=${translatedType}&u=${encodedUrl}`;

    // Fetch data
    const response = await fetch(apiUrl, {
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.text();
    return { data, error: null };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    return { data: null, error: errorMessage };
  }
}

